import React, { ComponentType, FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { AsyncThunkAction } from '@reduxjs/toolkit';

import { RequestType } from 'app/pages/order/redux/model/order-request';
import { OrderRequestsUtils } from 'app/pages/order/utils/order-requests-utils';
import { selectOrderRequests } from 'app/pages/order/redux/order-selector';
import { Spinner } from 'app/components/spinner';
import { AppDispatch, AppState } from 'app/redux/types/types';
import { useDispatchRequestAndHandleErrors } from 'app/hooks/use-dispatch-request-and-handle-errors';
import { useAppDispatch } from 'app/hooks/thunk-hooks';

export interface RequestToExecute {
    actionToDispatch: AsyncThunkAction<unknown, unknown, { dispatch: AppDispatch; state: AppState }>;
    requestType: RequestType;
    redirectTo?: () => string | undefined;
}

interface Props {
    requests: RequestToExecute[];
    loadingComponent?: ComponentType;
}

export const ProtectedContentWithoutCompletedRequests: FC<PropsWithChildren<Props>> = (props) => {
    const { children, requests, loadingComponent: LoadingComponent = Spinner } = props;
    const orderRequests = useSelector(selectOrderRequests);
    const startRequest = useDispatchRequestAndHandleErrors();
    const dispatch = useAppDispatch();

    const executeRequest = useCallback(async () => {
        const requestsToExecute = requests
            .filter((request) => OrderRequestsUtils.shouldCreateRequest(orderRequests, request.requestType))
            .map((request) =>
                request.requestType !== RequestType.GET_PARTNERS
                    ? startRequest(request.actionToDispatch)
                    : dispatch(request.actionToDispatch),
            );

        if (requestsToExecute.length) {
            await Promise.all(requests);
        }
    }, [dispatch, orderRequests, requests, startRequest]);

    useEffect(() => {
        executeRequest();
    }, [executeRequest]);

    if (!OrderRequestsUtils.isRequestsCompleted(orderRequests, ...requests.map((request) => request.requestType))) {
        return <LoadingComponent />;
    }

    const pageToRedirect = requests.map((request) => request.redirectTo?.()).find(Boolean);

    if (pageToRedirect) {
        return <Navigate to={pageToRedirect} replace={true} />;
    }

    return <>{children}</>;
};
